/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useMemo, useReducer } from "react";
import { enhancementsReducer } from "components/EnhancedSpeech/useEnhanceSpeech/Reducer";
import { useProcessEnhanceSpeechFile } from "components/EnhancedSpeech/useEnhanceSpeech/useProcessEnhanceSpeechFile";
import { useEnhanceSpeechPolling } from "components/EnhancedSpeech/useEnhanceSpeech/useEnhanceSpeechPolling";
import { useLoadExistingEnhanceSpeechTracks } from "components/EnhancedSpeech/useEnhanceSpeech/useLoadExistingEnhanceSpeechTracks";
import {
  EnhancementObject,
  ProcessingState,
} from "./useEnhanceSpeech/EnhancementObject";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";

export * from "./useEnhanceSpeech/EnhancementObject";

export interface EnhanceSpeechHook {
  processFile: (file: File, modelVersion: ModelVersion) => Promise<void>;
  enhancements: EnhancementObject[];
  isLoadingInitial: boolean;
}

export const useEnhanceSpeech: () => EnhanceSpeechHook = () => {
  const [enhancements, dispatch] = useReducer(enhancementsReducer, []);

  useEnhanceSpeechPolling(enhancements, dispatch);

  const processFile = useProcessEnhanceSpeechFile(dispatch);
  const isLoadingInitial = useLoadExistingEnhanceSpeechTracks(dispatch);

  const alwaysProcessingEnhancements =
    useAlwaysProcessingEnhancements(enhancements);

  return {
    processFile,
    enhancements: alwaysProcessingEnhancements,
    isLoadingInitial,
  };
};

const useAlwaysProcessingEnhancements = (
  enhancements: EnhancementObject[]
): EnhancementObject[] => {
  return useMemo(() => {
    const sortedEnhancements = [...enhancements].sort((a, b) => {
      const aCreatedAt = a.track.createdAt ?? "";
      const bCreatedAt = b.track.createdAt ?? "";
      return -aCreatedAt.localeCompare(bCreatedAt);
    });

    return enhancements.map((enhancement) => {
      if (enhancement.processingState !== ProcessingState.queued) {
        return enhancement;
      }

      const sortedIdx = sortedEnhancements.findIndex(
        (sortedEnhancement) =>
          sortedEnhancement.track.id === enhancement.track.id
      );

      let showAsProcessing = false;
      if (sortedIdx === sortedEnhancements.length - 1) {
        showAsProcessing = true;
      } else {
        const nextEnhancement = sortedEnhancements[sortedIdx + 1];
        if (nextEnhancement.processingState === ProcessingState.finished) {
          showAsProcessing = true;
        }
      }

      if (!showAsProcessing) {
        return enhancement;
      } else {
        return { ...enhancement, processingState: ProcessingState.processing };
      }
    });
  }, [enhancements]);
};
